import { Message, MessageDraft } from '../models/message';
import { markTurnStatus, turnKeyOf } from './reconcile';

/**
 * Answer drafts: the answer so far for a turn, pushed over SignalR (`ReceiveMessageDraft`) while the concierge is
 * still writing it.
 *
 * Drafts live beside the message list, never in it, so reconciliation and the turn lifecycle are untouched:
 * - a draft replaces the previous one of its turn (each carries the whole answer so far, and is shorter when the
 *   server restarts the answer);
 * - a draft never counts as the answer: the typing indicator and the soft timeout run until the final message;
 * - a draft does prove the server accepted the guest's message, which then shows as sent;
 * - a draft shows only while its turn waits for its answer, so the final message (over SignalR or from history), a
 *   failed send or the soft timeout ends it, and a draft that arrives after any of them is never shown.
 */

/** Drafts by turn key (`clientTurnId`, else the server `turnId`). */
export type DraftsByTurn = Record<string, MessageDraft>;

/** Replaces the turn's draft (never appends to it). */
export function applyDraft(drafts: DraftsByTurn, draft: MessageDraft): DraftsByTurn {
   return { ...drafts, [draft.turnKey]: draft };
}

/** Turn keys of drafts whose turn is not one of `turns`. */
export function staleDraftTurns(drafts: DraftsByTurn, turns: string[]): string[] {
   return Object.keys(drafts).filter((key) => !turns.includes(key));
}

/** Keeps only the drafts of `turns`. Returns the same object when nothing is dropped. */
export function keepDraftsOf(drafts: DraftsByTurn, turns: string[]): DraftsByTurn {
   const stale = staleDraftTurns(drafts, turns);
   if (stale.length === 0) return drafts;
   const next = { ...drafts };
   stale.forEach((key) => delete next[key]);
   return next;
}

function hasContent(draft: MessageDraft): boolean {
   return draft.blocks.length > 0 || draft.text.trim() !== '';
}

const NO_DRAFTS: MessageDraft[] = [];

/**
 * The drafts to show, in the order their turns were sent: of turns still waiting for their answer, with something to
 * read, and not answered on screen yet (the final message is on screen a render before its turn stops waiting).
 * Returns one shared empty array when there are none.
 */
export function selectVisibleDrafts(drafts: DraftsByTurn, awaitingTurns: string[], isAnswered: (turnKey: string) => boolean): MessageDraft[] {
   const visible: MessageDraft[] = [];
   awaitingTurns.forEach((turnKey) => {
      const draft = drafts[turnKey];
      if (draft && hasContent(draft) && !isAnswered(turnKey)) visible.push(draft);
   });
   return visible.length > 0 ? visible : NO_DRAFTS;
}

/**
 * A draft of a turn's answer proves the server accepted the guest's message while its POST is still open (the POST
 * returns only when the whole turn is done): a pending local guest message of the turn shows as sent, exactly as when
 * the POST returns. A failed or conflicting message is left to the send's own outcome. Returns the same array when
 * nothing changes.
 */
export function markDraftedTurnSent(messages: Message[], clientTurnId: string): Message[] {
   const pending = messages.some((m) => m.sender === 'user' && m.isOptimistic && turnKeyOf(m) === clientTurnId && m.deliveryStatus === 'pending');
   return pending ? markTurnStatus(messages, clientTurnId, 'sent') : messages;
}
